<i18n src="./translations"></i18n>

<template>
  <div
    v-if="visiblePills.length > 1"
    :class="[classes]"
    :data-preload="[$options.name]"
  >
    <ul
      ref="pillsRef"
      class="pills"
      :class="{ 'pills--collapsed': !isShowingMore }"
    >
      <template v-for="(pill, index) in displayedPills">
        <li
          v-if="!pill.isActive"
          :key="`${pill.alias}-inactive`"
          class="pill"
          :aria-pressed="false"
          role="button"
          tabindex="0"
          data-ssel-click="genart_pill"
          :data-ssel-label="pill.title"
          :data-ssel-position="index"
          :data-ssel-context="pillTrackingContext(pill)"
          :data-ssel-target-url="resolveTargetUrl(pill.alias)"
          @click="handlePillClick(pill.alias)"
          @keydown.enter.prevent="handlePillClick(pill.alias)"
          @keydown.space.prevent="handlePillClick(pill.alias)"
        >
          <span class="pill-label">
            {{ pill.title }}
            <span v-if="isVehicleSelected" class="pill-count"
              >({{ pill.count }})</span
            >
          </span>
        </li>
        <li v-else :key="`${pill.alias}-active`" class="pill active">
          <button
            class="clear-selection"
            :aria-label="`${pill.title} – ${$t(
              'gen-art-filter-container.clearSelection'
            )}`"
            @click="clearSelection()"
          >
            <span class="pill-label">
              {{ pill.title }}
              <span v-if="isVehicleSelected" class="pill-count"
                >({{ pill.count }})</span
              >
            </span>
            <Icon name="close" aria-hidden="true" width="24" height="24" />
          </button>
        </li>
      </template>

      <li
        v-if="!isShowingMore && hasOverflow"
        class="pill show-more"
        role="button"
        tabindex="0"
        @click="expand"
        @keydown.enter.prevent="expand"
        @keydown.space.prevent="expand"
      >
        <Icon
          name="add"
          class="pill-icon"
          aria-hidden="true"
          width="24"
          height="24"
        />
        {{ hiddenCount }} {{ $t('gen-art-filter-container.more') }}
      </li>

      <li
        v-if="isShowingMore && hasOverflow"
        class="pill show-less"
        role="button"
        tabindex="0"
        @click="collapse"
        @keydown.enter.prevent="collapse"
        @keydown.space.prevent="collapse"
      >
        <Icon
          name="chevron_up"
          class="pill-icon"
          aria-hidden="true"
          width="24"
          height="24"
        />
        {{ $t('gen-art-filter-container.less') }}
      </li>
    </ul>
  </div>
</template>

<script>
import globalMixin from 'Libs/mixins/globalMixin';
import Icon from 'Components/00-generated/Icon';
import SessionStorage from 'Libs/helpers/session-storage';

const EXPANDED_KEY = 'genartExpanded';

export default {
  name: 'GenArtFilterContainer',
  components: { Icon },
  mixins: [globalMixin],
  props: {
    options: {
      type: Object,
      default: () => ({}),
    },
    value: {
      type: Array,
      default: () => [],
    },
    isVehicleSelected: {
      type: Boolean,
      default: false,
    },
    resolveTargetUrl: {
      type: Function,
      default: () => undefined,
    },
  },
  data() {
    return {
      isShowingMore: false,
      hasOverflow: false,
      firstRowCount: Infinity,
      hiddenCount: 0,
      pillsWidth: null,
    };
  },
  computed: {
    visiblePills() {
      return Object.values(this.options)
        .filter((o) => !this.isVehicleSelected || o.count > 0)
        .sort((a, b) => a.title.localeCompare(b.title))
        .map((o) => ({ ...o, isActive: this.value.includes(o.alias) }));
    },
    displayedPills() {
      if (this.isShowingMore || !this.hasOverflow) return this.visiblePills;
      return this.visiblePills.slice(0, this.firstRowCount);
    },
  },
  watch: {
    options() {
      this.hasOverflow = false;
      this.$nextTick(() => this.checkOverflow());
    },
    isVehicleSelected() {
      this.hasOverflow = false;
      this.$nextTick(() => this.checkOverflow());
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.checkOverflow();
      this.expandIfActivePillHidden();
    });
    this.resizeObserver = new ResizeObserver((entries) =>
      this.handleResize(entries)
    );
    this.resizeObserver.observe(this.$refs.pillsRef);
  },
  beforeDestroy() {
    this.resizeObserver?.disconnect();
  },
  methods: {
    pillTrackingContext(pill) {
      return JSON.stringify({
        alias: pill.alias,
        count: String(pill.count),
        position_total: String(this.displayedPills.length),
      });
    },
    handlePillClick(alias) {
      this.$emit('input', [alias]);
      this.scrollPillIntoView();
    },
    clearSelection() {
      const activeIndex = this.visiblePills.findIndex((p) => p.isActive);

      if (
        this.isShowingMore &&
        this.hasOverflow &&
        activeIndex !== -1 &&
        activeIndex >= this.firstRowCount
      ) {
        new SessionStorage().setItem(EXPANDED_KEY, '1');
      }

      this.$emit('input', []);
    },
    expand() {
      this.isShowingMore = true;
    },
    collapse() {
      this.isShowingMore = false;
    },
    checkOverflow() {
      const container = this.$refs.pillsRef;
      if (!container) return;

      const pills = Array.from(
        container.querySelectorAll('.pill:not(.show-more):not(.show-less)')
      );
      if (!pills.length) return;

      const firstRowTop = pills[0].offsetTop;
      const firstRowPills = pills.filter((p) => p.offsetTop === firstRowTop);
      const overflowPills = pills.filter((p) => p.offsetTop > firstRowTop);

      if (overflowPills.length > 0) {
        this.firstRowCount = Math.max(1, firstRowPills.length - 1);
        this.hiddenCount = pills.length - this.firstRowCount;
        this.hasOverflow = true;
      } else {
        this.firstRowCount = Infinity;
        this.hiddenCount = 0;
        this.hasOverflow = false;
      }
    },
    handleResize(entries) {
      const width = entries?.[0]?.contentRect?.width;
      if (width === this.pillsWidth) return;

      this.pillsWidth = width;
      this.isShowingMore = false;

      this.hasOverflow = false;
      this.$nextTick(() => this.checkOverflow());
    },
    expandIfActivePillHidden() {
      if (!this.hasOverflow) return;

      const storage = new SessionStorage();
      if (storage.getItem(EXPANDED_KEY) === '1') {
        storage.removeItem(EXPANDED_KEY);
        this.isShowingMore = true;
        return;
      }

      const activeIndex = this.visiblePills.findIndex((p) => p.isActive);
      if (activeIndex !== -1 && activeIndex >= this.firstRowCount) {
        this.isShowingMore = true;
      }
    },
    scrollPillIntoView() {
      this.$nextTick(() => {
        const activePill = this.$el && this.$el.querySelector('.pill.active');
        if (activePill) {
          activePill.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
          });
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'variables/breakpoints';
@import 'mixins';

.genArtFilterContainer {
  background-color: var(--color-white);
  padding-bottom: var(--space-3);

  .pills {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-1);
    padding-bottom: var(--space-0--half);

    &--collapsed {
      max-height: calc(var(--space-6) + var(--space-0--half));
      overflow-y: hidden;
    }
  }

  .pill {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
    height: var(--space-6);
    padding: var(--space-0--half) var(--space-2--half);
    border: 1px solid var(--color-silver-lining);
    border-radius: var(--border-radius-default);
    background-color: var(--color-white);
    font-size: var(--font-size-SM);
    color: var(--color-nero);
    cursor: pointer;
    white-space: nowrap;
    transition: border-color var(--time-XS) ease-in-out;
    user-select: none;

    &:hover {
      border-color: var(--color-secondary);
    }

    &.active {
      border: 2px solid var(--color-secondary);
      padding:
        var(--space-0--half) var(--space-1--half) var(--space-0--half)
        var(--space-2--half);
    }

    &.show-more,
    &.show-less {
      color: var(--color-nero);
      gap: var(--space-1);
      padding:
        var(--space-0--half) var(--space-2--half) var(--space-0--half)
        var(--space-2);

      &:hover {
        border-color: var(--color-secondary);
        color: var(--color-secondary);
      }
    }
  }

  .pill-label {
    line-height: 1;
  }

  .pill-count {
    color: var(--color-charcoal-gray);
  }

  .pill-icon {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  .clear-selection {
    appearance: none;
    background: none;
    border: none;
    color: var(--color-nero);
    cursor: pointer;
    font-size: inherit;
    line-height: 1;
    padding: 0;
    display: flex;
    align-items: center;
    gap: var(--space-1);

    &:hover {
      color: var(--color-secondary);
    }
  }
}

@media #{$_mediaMDown} {
  .genArtFilterContainer {
    padding: var(--space-1) 0 0;

    .pills {
      flex-wrap: nowrap;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      scroll-behavior: smooth;
      padding-bottom: var(--space-1--half);

      &::-webkit-scrollbar {
        display: none;
      }

      -ms-overflow-style: none;
      scrollbar-width: none;
    }

    .pill {
      flex-shrink: 0;
    }

    .show-more,
    .show-less {
      display: none;
    }
  }
}
</style>
